/**
 * Account — professional multi-tab ledger UI (mobile-first).
 * Real GBUX only; identity links unlock fleet/Crossmint — never free chips.
 */
import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { useWalletState } from "@/contexts/wallet-context";
import { WalletConnectPanel } from "@/components/wallet-connect-panel";
import { BuyGbuxModal } from "@/components/buy-gbux-modal";
import { formatPlayGbux } from "@shared/gbux-math";
import {
  isLoggedIn,
  loginWithGrudgeId,
  openGrudgeWalletSite,
} from "@/lib/grudge-auth";
import { ACCOUNT_LINKS, FLEET, GBUX } from "@shared/fleet";
import { BRAND } from "@shared/brand";
import {
  ArrowLeft,
  ArrowRightLeft,
  CircleHelp,
  Coins,
  ExternalLink,
  History,
  LayoutDashboard,
  Link2,
  Mail,
  MessageCircle,
  RefreshCw,
  ShieldCheck,
  Trophy,
  UserCircle,
  Wallet,
  Gamepad2,
} from "lucide-react";
import { AvatarAppearanceEditor } from "@/components/avatar-appearance-editor";
import { AccountHelpPanel } from "@/components/account-help-panel";
import { PlayTransferPanel } from "@/components/play-transfer-panel";
import "@/styles/account.css";

export default function UserAccount() {
  const { wallet, refreshBalances, openWalletPanel } = useWalletState();
  const [, setLocation] = useLocation();
  const [buyOpen, setBuyOpen] = useState(false);
  const [tab, setTab] = useState(wallet.publicKey ? "overview" : "wallet");
  const pk = wallet.publicKey;

  const headers = useMemo(() => {
    const h: Record<string, string> = {};
    if (pk) h["X-Wallet-Address"] = pk;
    return h;
  }, [pk]);

  const scopesQ = useQuery({
    queryKey: ["wallet-scopes", pk],
    enabled: !!pk,
    queryFn: async () => {
      const r = await fetch(
        `/api/wallet/scopes?wallet=${encodeURIComponent(pk!)}`,
        { headers },
      );
      if (!r.ok) throw new Error("scopes failed");
      return r.json();
    },
    refetchInterval: 12000,
  });

  const profileQ = useQuery({
    queryKey: ["poker-profile", pk],
    enabled: !!pk,
    queryFn: async () => {
      const r = await fetch(
        `/api/user-profile?wallet=${encodeURIComponent(pk!)}`,
        { headers },
      );
      if (!r.ok) throw new Error("profile failed");
      return r.json();
    },
    refetchInterval: 15000,
  });

  const historyQ = useQuery({
    queryKey: ["poker-history", pk],
    enabled: !!pk,
    queryFn: async () => {
      const r = await fetch(
        `/api/user/history?wallet=${encodeURIComponent(pk!)}`,
        { headers },
      );
      if (!r.ok) throw new Error("history failed");
      return r.json();
    },
    refetchInterval: 20000,
  });

  const playGbux = Number(
    scopesQ.data?.playWallet?.balance ?? wallet.playGbux ?? 0,
  );
  const sol = scopesQ.data?.connectedWallet?.sol ?? wallet.chainSol ?? 0;
  const gbuxChain =
    scopesQ.data?.connectedWallet?.gbuxOnChain ?? wallet.chainGbux ?? 0;
  const custodial = scopesQ.data?.custodialWallet;
  const usd = scopesQ.data?.connectedWallet?.usdEstimate;
  const transactions = historyQ.data?.transactions ?? [];
  const hands = historyQ.data?.hands ?? [];
  const sessions = historyQ.data?.sessions ?? [];
  const user = profileQ.data;
  const fleetBag =
    scopesQ.data?.fleetBag?.gbux != null
      ? Number(scopesQ.data.fleetBag.gbux)
      : wallet.accountGbux || 0;

  const refreshAll = () => {
    void refreshBalances();
    void scopesQ.refetch();
    void profileQ.refetch();
    void historyQ.refetch();
  };

  return (
    <div className="account-page">
      <div
        className="account-ambient"
        style={{ backgroundImage: `url(${BRAND.account.bg})` }}
        aria-hidden
      />

      <div className="account-shell">
        <header className="account-topbar">
          <div className="account-brand">
            <Button
              variant="outline"
              size="sm"
              className="border-amber-500/30 shrink-0"
              onClick={() => setLocation("/lobby")}
            >
              <ArrowLeft className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Lobby</span>
            </Button>
            <img
              src={BRAND.account.badge}
              alt=""
              className="account-badge"
              width={44}
              height={44}
            />
            <div className="min-w-0">
              <h1>Account</h1>
              <p>
                {BRAND.shortName} · play ledger · Solana mainnet · Grudge ID
              </p>
            </div>
          </div>
          <div className="account-actions">
            <Button
              variant="outline"
              size="sm"
              className="border-zinc-700"
              onClick={refreshAll}
            >
              <RefreshCw className="h-3.5 w-3.5 sm:mr-1" />
              <span className="hidden xs:inline sm:inline">Refresh</span>
            </Button>
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-500"
              onClick={() => openWalletPanel("deposit")}
            >
              <Coins className="h-3.5 w-3.5 sm:mr-1" />
              Play fund
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-zinc-700 hidden sm:inline-flex"
              onClick={() => setBuyOpen(true)}
            >
              Buy options
            </Button>
            <Button
              size="sm"
              variant="secondary"
              className="hidden md:inline-flex"
              onClick={() => setLocation("/slots")}
            >
              <Gamepad2 className="h-3.5 w-3.5 mr-1" />
              Slots
            </Button>
          </div>
        </header>

        {/* Quick balance strip — always visible */}
        <div className="account-stat-grid">
          <div className="account-stat play">
            <div className="label">Play ledger</div>
            <div className="value">{formatPlayGbux(playGbux)}</div>
          </div>
          <div className="account-stat chain">
            <div className="label">On-chain GBUX</div>
            <div className="value">{Number(gbuxChain).toLocaleString()}</div>
          </div>
          <div className="account-stat">
            <div className="label">SOL</div>
            <div className="value">{Number(sol).toFixed(4)}</div>
          </div>
          <div className="account-stat fleet">
            <div className="label">Fleet bag</div>
            <div className="value">
              {fleetBag != null && fleetBag > 0
                ? Number(fleetBag).toLocaleString()
                : "—"}
            </div>
          </div>
        </div>

        <div className="account-layout">
          <aside className="account-side">
            <WalletConnectPanel />
            <div className="account-card">
              <div className="account-card-head">
                <Link2 className="h-3.5 w-3.5 text-violet-300" />
                Connect identity
              </div>
              <div className="account-card-body space-y-2">
                <p className="hint">
                  Grudge ID is who you are. Phantom is how you sign. Play
                  ledger sits tables. Linking does not mint extra GBUX (welcome
                  is already on the play ledger).
                </p>
                <div className="account-link-grid">
                  <Button
                    size="sm"
                    className="w-full justify-start bg-violet-800 hover:bg-violet-700"
                    onClick={() => loginWithGrudgeId("/account")}
                  >
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Grudge ID
                    {isLoggedIn() && (
                      <Badge className="ml-auto bg-emerald-700/60 text-[10px]">
                        linked
                      </Badge>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full justify-start border-zinc-700"
                    onClick={() =>
                      window.open(
                        ACCOUNT_LINKS.email.href,
                        "_blank",
                        "noopener",
                      )
                    }
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Email
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full justify-start border-zinc-700"
                    onClick={() =>
                      window.open(
                        ACCOUNT_LINKS.discord.href,
                        "_blank",
                        "noopener",
                      )
                    }
                  >
                    <MessageCircle className="mr-2 h-4 w-4" />
                    Discord
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full justify-start border-zinc-700"
                    onClick={() =>
                      window.open(FLEET.puterDashboard, "_blank", "noopener")
                    }
                  >
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    Puter dashboard
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full justify-start border-zinc-700"
                    onClick={() => openGrudgeWalletSite()}
                  >
                    <Wallet className="mr-2 h-4 w-4" />
                    Grudge Wallet
                    <ExternalLink className="ml-auto h-3.5 w-3.5 opacity-60" />
                  </Button>
                </div>
              </div>
            </div>
          </aside>

          <main className="account-main">
            <div className="account-balance-row">
              <div className="account-card play">
                <div className="account-card-head text-emerald-300/90">
                  <Coins className="h-3.5 w-3.5" />
                  Play wallet
                </div>
                <div className="account-card-body">
                  <div className="big text-emerald-300">
                    {formatPlayGbux(playGbux)}
                  </div>
                  <p className="hint">
                    Tables · slots · blackjack. Verified deposit only.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-500"
                      onClick={() => openWalletPanel("deposit")}
                    >
                      Wallet → play
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-emerald-600/40 text-emerald-200"
                      onClick={() => setTab("send")}
                    >
                      <ArrowRightLeft className="h-3.5 w-3.5 mr-1" />
                      Send user
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-zinc-700"
                      onClick={() => setBuyOpen(true)}
                    >
                      Fund options
                    </Button>
                  </div>
                </div>
              </div>

              <div className="account-card chain">
                <div className="account-card-head text-cyan-300/90">
                  <Wallet className="h-3.5 w-3.5" />
                  On-chain
                </div>
                <div className="account-card-body space-y-1 text-sm">
                  <div>
                    <span className="text-zinc-500">SOL </span>
                    <strong className="text-cyan-100">
                      {Number(sol).toFixed(4)}
                    </strong>
                    {usd && (
                      <span className="text-zinc-500 text-xs">
                        {" "}
                        (~${Number(usd.solUsd).toFixed(2)})
                      </span>
                    )}
                  </div>
                  <div>
                    <span className="text-zinc-500">GBUX SPL </span>
                    <strong className="text-cyan-200">
                      {Number(gbuxChain).toLocaleString()}
                    </strong>
                  </div>
                  {custodial?.address && (
                    <div className="pt-1">
                      <span className="account-chip">Studio bag wallet</span>
                      <div className="account-mono mt-1">
                        {String(custodial.address).slice(0, 8)}…
                        {String(custodial.address).slice(-6)}
                      </div>
                    </div>
                  )}
                  <p className="hint">
                    Mint {GBUX.mint.slice(0, 6)}… · mainnet-beta
                  </p>
                </div>
              </div>

            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="account-tabs-list">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="send">
                  <ArrowRightLeft className="mr-1 h-3 w-3" />
                  Send
                </TabsTrigger>
                <TabsTrigger value="wallet">
                  <Wallet className="mr-1 h-3 w-3" />
                  Wallet
                </TabsTrigger>
                <TabsTrigger value="appearance">
                  <UserCircle className="mr-1 h-3 w-3" />
                  Look
                </TabsTrigger>
                <TabsTrigger value="history">
                  <History className="mr-1 h-3 w-3" />
                  Ledger
                </TabsTrigger>
                <TabsTrigger value="hands">
                  <Trophy className="mr-1 h-3 w-3" />
                  Hands
                </TabsTrigger>
                <TabsTrigger value="sessions">Sessions</TabsTrigger>
                <TabsTrigger value="help">
                  <CircleHelp className="mr-1 h-3 w-3" />
                  Help
                </TabsTrigger>
              </TabsList>

              <TabsContent value="send" className="account-tabs-panel mt-3">
                {!pk ? (
                  <p className="text-sm text-zinc-500 py-8 text-center">
                    Connect a wallet to send play GBUX to another username.
                  </p>
                ) : (
                  <PlayTransferPanel
                    playGbux={Number(playGbux)}
                    onDone={refreshAll}
                  />
                )}
              </TabsContent>

              <TabsContent value="overview" className="account-tabs-panel mt-3">
                <h2 className="text-sm font-semibold text-amber-100/90 mb-2">
                  How balances work
                </h2>
                <div className="space-y-3 text-sm text-zinc-300 leading-relaxed">
                  <p>
                    <strong className="text-emerald-300">Play</strong> — real
                    server-side wallet for tables, slots &amp; blackjack. Fund
                    via <strong>Wallet → Transfer</strong> (on-chain GBUX SPL
                    1:1 into play).{" "}
                    <strong>Send</strong> = player → player on this app only
                    (server ledger ↔ server ledger).
                  </p>
                  <p>
                    <strong className="text-cyan-300">On-chain</strong> — GBUX
                    in Phantom (mainnet). Games never spend it until you deposit
                    to play.
                  </p>
                  <p>
                    <strong className="text-amber-300">Cash out / swap out</strong>{" "}
                    — staff email only:{" "}
                    <a
                      className="text-amber-300 underline underline-offset-2"
                      href="mailto:games@grudgestudio.org?subject=BUDB%20cash-out%20/%20swap"
                    >
                      games@grudgestudio.org
                    </a>{" "}
                    (Account → Help ticket). Not an in-app chain send.
                  </p>
                  <p>
                    <strong className="text-violet-300">Grudge ID</strong> —{" "}
                    <a
                      className="text-violet-300 underline underline-offset-2"
                      href="https://id.grudge-studio.com/login"
                    >
                      id.grudge-studio.com
                    </a>{" "}
                    who you are. Phantom Connect for play ledger Send.
                  </p>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                  <MiniStat
                    label="Hands"
                    value={String(
                      user?.handsPlayed ?? user?.stats?.hands_played ?? 0,
                    )}
                  />
                  <MiniStat
                    label="Won"
                    value={String(
                      user?.handsWon ?? user?.stats?.hands_won ?? 0,
                    )}
                  />
                  <MiniStat
                    label="Winnings"
                    value={Number(
                      user?.totalWinnings ?? user?.stats?.total_winnings ?? 0,
                    ).toLocaleString()}
                  />
                  <MiniStat
                    label="Level"
                    value={String(user?.accountLevel ?? "—")}
                  />
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button size="sm" onClick={() => setLocation("/lobby")}>
                    Poker lobby
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-zinc-700"
                    onClick={() => setLocation("/slots")}
                  >
                    Ethereal Treasure
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-zinc-700"
                    onClick={() => openGrudgeWalletSite()}
                  >
                    Open wallet site
                    <ExternalLink className="ml-1 h-3 w-3" />
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="wallet" className="account-tabs-panel mt-3">
                <p className="text-xs text-zinc-500 mb-3">
                  Connect injected wallets and fund play. Network is always
                  Solana mainnet.
                </p>
                <div className="md:hidden">
                  <WalletConnectPanel />
                </div>
                <p className="text-xs text-zinc-500 mt-2 hidden md:block">
                  Use the panel on the left (desktop) or open Transfer / Fund
                  options above.
                </p>
                <Button
                  className="mt-3 w-full sm:w-auto bg-amber-600 hover:bg-amber-500"
                  onClick={() => openWalletPanel("deposit")}
                >
                  Open wallet drawer
                </Button>
              </TabsContent>

              <TabsContent
                value="appearance"
                className="account-tabs-panel mt-3"
              >
                <h2 className="text-sm font-semibold text-amber-100/90 mb-1">
                  Table appearance
                </h2>
                <p className="text-xs text-zinc-500 mb-3">
                  Avatar, name color, frame — saved to your play account.
                </p>
                {!pk ? (
                  <p className="text-sm text-zinc-500 py-8 text-center">
                    Connect a wallet to set how you appear at tables.
                  </p>
                ) : (
                  <AvatarAppearanceEditor
                    wallet={pk}
                    initial={{
                      username: user?.username,
                      avatarId:
                        user?.avatarId || user?.appearance?.avatarId,
                      nameColor:
                        user?.nameColor || user?.appearance?.nameColor,
                      frameColor:
                        user?.frameColor || user?.appearance?.frameColor,
                      avatarScale:
                        user?.avatarScale ?? user?.appearance?.avatarScale,
                    }}
                    onSaved={() => void profileQ.refetch()}
                  />
                )}
              </TabsContent>

              <TabsContent value="history" className="account-tabs-panel mt-3">
                <LedgerTable
                  rows={transactions}
                  empty="No ledger yet. Deposit real GBUX to play."
                />
              </TabsContent>

              <TabsContent value="hands" className="account-tabs-panel mt-3">
                <div className="space-y-2 text-sm">
                  {hands.length === 0 && (
                    <p className="text-zinc-500 py-6 text-center">
                      No hands yet.
                    </p>
                  )}
                  {hands.map((h: Record<string, unknown>) => (
                    <div
                      key={String(h.id)}
                      className="flex flex-wrap justify-between gap-2 rounded-xl border border-white/5 bg-black/30 p-3"
                    >
                      <span className="text-zinc-300">
                        {String(h.table_id)} #{String(h.hand_number)}
                      </span>
                      <span className="text-amber-300 font-medium">
                        {String(h.winner_name || "?")} ·{" "}
                        {Number(h.pot_amount).toLocaleString()} GBUX
                      </span>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="sessions" className="account-tabs-panel mt-3">
                <div className="space-y-2 text-sm">
                  {sessions.length === 0 && (
                    <p className="text-zinc-500 py-6 text-center">
                      No sessions yet.
                    </p>
                  )}
                  {sessions.map((s: Record<string, unknown>) => (
                    <div
                      key={String(s.id)}
                      className="flex flex-wrap justify-between gap-2 rounded-xl border border-white/5 bg-black/30 p-3"
                    >
                      <span>{String(s.table_id)}</span>
                      <span className="font-mono text-xs text-zinc-400">
                        in {Number(s.buy_in)} → out{" "}
                        {s.cash_out != null ? Number(s.cash_out) : "…"}
                      </span>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="help" className="account-tabs-panel mt-3">
                <AccountHelpPanel wallet={pk} playGbux={Number(playGbux)} />
              </TabsContent>
            </Tabs>
          </main>
        </div>
      </div>

      <BuyGbuxModal open={buyOpen} onOpenChange={setBuyOpen} />
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="account-stat">
      <div className="label">{label}</div>
      <div className="value" style={{ fontSize: "1.1rem" }}>
        {value}
      </div>
    </div>
  );
}

function LedgerTable({
  rows,
  empty,
}: {
  rows: Array<Record<string, unknown>>;
  empty: string;
}) {
  return (
    <div className="account-table-wrap">
      <table className="account-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Amount</th>
            <th>Note</th>
            <th>When</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="p-6 text-zinc-500 text-center">
                {empty}
              </td>
            </tr>
          )}
          {rows.map((t) => (
            <tr key={String(t.id)}>
              <td>{String(t.type)}</td>
              <td className="font-mono text-amber-200/90">
                {Number(t.amount).toLocaleString()}
              </td>
              <td className="text-xs text-zinc-400 max-w-[160px] truncate">
                {String(t.note ?? t.tableId ?? "")}
              </td>
              <td className="text-zinc-500 text-xs whitespace-nowrap">
                {String(t.timestamp ?? "")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
